import { ReactNode } from "react";
import { Popover, PopoverContent } from "@/common/Popover";

// const selectclsx = clsx([
//     // Basic layout
//     "relative block appearance-none rounded-lg",
//     // Horizontal padding
//     // Options (multi-select)
//     "[&_optgroup]:font-semibold",
//     // Typography
//     "text-base/6 text-zinc-950 placeholder:text-zinc-500 sm:text-sm/6 dark:text-white dark:*:text-white",
//     // Border
//     "border border-zinc-950/10 data-[hover]:border-zinc-950/20 dark:border-white/10 dark:data-[hover]:border-white/20",
//     // Background color
//     "bg-transparent dark:bg-white/10 dark:*:bg-zinc-800",
//     // Hide default focus styles
//     "focus:outline-none",
//     // Invalid state
//     "data-[invalid]:border-red-500 data-[invalid]:data-[hover]:border-red-500 data-[invalid]:dark:border-red-600 data-[invalid]:data-[hover]:dark:border-red-600",
//     // Disabled state
//     "data-[disabled]:border-zinc-950/20 data-[disabled]:opacity-100 dark:data-[hover]:data-[disabled]:border-white/15 data-[disabled]:dark:border-white/15 data-[disabled]:dark:bg-white/[2.5%]",
//     "w-40 z-20",
//     "text-left",
// ]);

/**
 * An optional heading over a run of options, so a long list reads as the few
 * places its entries come from. Same shape the audience filter dropdown uses:
 * an icon and a name per source, in source order.
 */
export type PopoverSelectGroup = {
    title: string;
    icon?: ReactNode;
    /** Option values belonging to this group, in the order to show them. */
    values: string[];
};

export default function PopoverAsSelect({
    onClose,
    options,
    groups,
    onChange,
}: {
    onClose: () => void;
    options: Record<string, string | number>;
    /**
     * Groups the options under headings. Anything a group does not claim is
     * listed ungrouped at the end, so the caller cannot lose an option by
     * forgetting it -- that is where the free-form escape hatch lands.
     */
    groups?: PopoverSelectGroup[];
    onChange: (value: string) => void;
}) {
    const option = (value: string) => (
        <div key={value} className={"full-w p-0.5"}>
            <div
                className="full-w px-2 py-1 hover:bg-zinc-950/5 hover:text-zinc-950 dark:hover:bg-white/10 dark:hover:text-white rounded-lg cursor-pointer"
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onChange(value.toString());
                }}
            >
                {options[value]}
            </div>
        </div>
    );

    const grouped = new Set((groups ?? []).flatMap(({ values }) => values));
    const ungrouped = Object.keys(options).filter(
        (value) => !grouped.has(value),
    );

    return (
        <Popover
            open={true}
            autoUpdatePosition={false}
            placement={"bottom-start"}
            onOpenChange={onClose}
        >
            {(popover) => (
                <div ref={popover.refs.setReference}>
                    <PopoverContent>
                        <div className={"p-1 min-w-32 text-default"}>
                            {(groups ?? []).map((group, index) => (
                                <div key={group.title}>
                                    {index > 0 && (
                                        <div
                                            className={
                                                "my-1 border-t border-zinc-950/10 dark:border-white/10"
                                            }
                                        />
                                    )}
                                    <div
                                        className={
                                            "flex items-center gap-2 px-2 py-1 text-xs font-medium text-subtle"
                                        }
                                    >
                                        {group.icon}
                                        {group.title}
                                    </div>
                                    {group.values
                                        .filter((value) => value in options)
                                        .map(option)}
                                </div>
                            ))}
                            {!!groups?.length && !!ungrouped.length && (
                                <div
                                    className={
                                        "my-1 border-t border-zinc-950/10 dark:border-white/10"
                                    }
                                />
                            )}
                            {ungrouped.map(option)}
                        </div>
                    </PopoverContent>
                </div>
            )}
        </Popover>
    );
}
